import { useCallback, useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./profile.module.scss";
import { RequiredFieldAstrick } from "components/Common/Common";
import { Formik } from "formik";
import AppInputField from "components/shared/FormComponents/AppInputField";
import { useCheckUsernameMutation, useGetProfileQuery, useUpdateProfileMutation } from "store/services/profile";
import LoadingIndicator from "components/shared/LoadingIndicator";
import LoadingOverlay from "components/shared/LoadingOverlay";
import profileSchema from "./profile.schema";
import { cloneDeep, debounce } from "lodash";
import { errorMessageToast, successMessageToast } from "helpers/common";
import AppProfilePictureField from "components/shared/FormComponents/AppProfilePictureField";
import { MIME_TYPES } from "config/constants";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";

const Profile = () => {

    const [initialValues, setInitialValues] = useState({
        lastname: "",
        firstname: "",
        username: "",
        phone: "",
        password: "",
        email: "",
        self_description: "",
        profile_picture: "",
        email_signature: "",
        signature_img: "",
    });

    const [usernameError, setUsernameError] = useState(null);

    const { data: profileDetails, isLoading: getProfileLoading, isFetching: getProfileFetching } = useGetProfileQuery();
    const [checkUsername, { isLoading: checkUsernameLoading }] = useCheckUsernameMutation();
    const [updateProfile, { isLoading: updateProfileLoading }] = useUpdateProfileMutation();

    const handleUsernameChange = useCallback(debounce(async (value, setFieldError) => {
        try {
            if (value) {
                const response = await checkUsername({ username: value });
                if (response?.data?.data?.status == "false") {
                    setFieldError("username", "Username already exist")
                    setUsernameError("Username already exist");
                    return;
                };
                setFieldError("username", "")
                setUsernameError(null);
            };
        } catch (err) { console.log("Error checking username", err) };
    }, [500]));

    const handleSubmit = async (values) => {
        try {

            if (usernameError) {
                errorMessageToast(usernameError);
                return;
            };

            const payload = cloneDeep(values);

            const result = await updateProfile(payload);
            if (result?.error) return;

            successMessageToast(result?.data?.message);
        } catch (err) { console.log("Error checking username", err) };
    };

    useEffect(() => {
        if (profileDetails) {
            const { lastname, firstname, username, phone, password, email, therapist_detail } = profileDetails || {};
            const { email_signature, profile_picture, self_description, signature_img } = therapist_detail || {};
            setInitialValues({ lastname, firstname, username, phone, password, email, email_signature, profile_picture, self_description, signature_img });
        };
    }, [profileDetails]);

    return (

        <Container fluid className={`${styles.addForm} position-relative py-4 addForm`} >

            <Row>
                <Col lg="12" className="my-2">
                    <h6 className="m-0 fw-bold themeBlue">Profile</h6>
                </Col>
                {getProfileLoading ? <LoadingIndicator /> : (
                    <Col lg="12" className="my-2">
                        {(getProfileFetching) && <LoadingOverlay />}
                        <Formik
                            initialValues={initialValues}
                            validationSchema={profileSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize
                        >
                            {({
                                handleSubmit,
                                setFieldError,
                            }) => (
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Profile Picture
                                            </label>
                                            <AppProfilePictureField
                                                name="profile_picture"
                                                acl={"public-read"}
                                                accept="image/*"
                                                allowedTypes={[MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.png]}
                                            />
                                        </Col>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Email
                                                <RequiredFieldAstrick />
                                            </label>
                                            <div className="d-flex w-100 align-items-center">
                                                <AppInputField
                                                    name="email"
                                                    type="text"
                                                    placeholder="Enter Email"
                                                    className={`${styles.formControl} form-control`}
                                                />
                                            </div>
                                        </Col>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Username
                                                <RequiredFieldAstrick />
                                            </label>
                                            <div className="d-flex w-100 position-relative">
                                                <AppInputField
                                                    name="username"
                                                    type="text"
                                                    placeholder="Enter Username"
                                                    className={`${styles.formControl} form-control`}
                                                    onInputChange={(value) => handleUsernameChange(value, setFieldError)}
                                                    maxLength={100}
                                                />
                                                {checkUsernameLoading && <div style={{ right: "10px", top: "20px" }} className="position-absolute">
                                                    <LoadingIndicator inline />
                                                </div>}
                                            </div>
                                        </Col>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Password
                                                <RequiredFieldAstrick />
                                            </label>
                                            <AppInputField
                                                autoComplete='off'
                                                name="password"
                                                type="password"
                                                placeholder="Enter Password"
                                                className={`${styles.formControl} form-control`}
                                            />
                                        </Col>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Last Name
                                                <RequiredFieldAstrick />
                                            </label>
                                            <AppInputField
                                                name="lastname"
                                                type="text"
                                                placeholder="Enter Last Name"
                                                className={`${styles.formControl} form-control`}
                                            />
                                        </Col>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                First Name
                                                <RequiredFieldAstrick />
                                            </label>
                                            <AppInputField
                                                name="firstname"
                                                type="text"
                                                placeholder="Enter First Name"
                                                className={`${styles.formControl} form-control`}
                                            />
                                        </Col>
                                        <Col sm="6" md="3" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Phone
                                                <RequiredFieldAstrick />
                                            </label>
                                            <AppInputField
                                                name="phone"
                                                type="text"
                                                placeholder="Enter Phone"
                                                className={`${styles.formControl} form-control`}
                                                maxLength={25}
                                            />
                                        </Col>
                                        <Col sm="6" md="6" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                About{" "}
                                                <p className="text-muted fw-normal inline-block ms-1 m-0">
                                                    (Description)
                                                </p>
                                            </label>
                                            <AppInputField
                                                name="self_description"
                                                placeholder="About (Description)"
                                                fieldType="textarea"
                                                rows="4"
                                                className={`${styles.formControl} form-control`}
                                                maxLength={500}
                                                style={{ height: "100px" }}
                                            />
                                        </Col>
                                        <Col sm="6" md="6" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Email Signature
                                            </label>
                                            <AppInputField
                                                fieldType="textarea"
                                                name="email_signature"
                                                rows={4}
                                                cols={4}
                                                placeholder="John Doe&#10;john@example.com&#10;+1 222 222 2222&#10;Street 10, NY, USA "
                                                className={`${styles.formControl} form-control`}
                                                maxLength={500}
                                                style={{ height: "100px" }}
                                            />
                                        </Col>
                                        <Col sm="6" md="6" className="my-2">
                                            <label
                                                htmlFor=""
                                                className="form-label themeBlue fw-bold mb-0 d-flex"
                                            >
                                                Upload Signature
                                            </label>
                                            <AppFileUploadField
                                                name="signature_img"
                                                acl={"public-read"}
                                                accept="image/png"
                                                allowedTypes={[MIME_TYPES.png]}
                                                className={`${styles.formControl} form-control`}
                                                preview
                                            />
                                        </Col>
                                        <Col className="my-2 mt-4" lg="12">
                                            <div
                                                className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                                                style={{ borderTop: "1px solid #41a2a84d" }}
                                            >
                                                <Button
                                                    type="submit"
                                                    className="d-flex align-items-center justify-content-center commonBtn"
                                                    disabled={updateProfileLoading}
                                                >
                                                    {updateProfileLoading ? <LoadingIndicator inline /> : "Update"}

                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>

                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </Col>
                )}
            </Row>
        </Container>

    );
};

export default Profile;
